import React, { useRef, useState } from "react";
import { CheckOutlined, ExclamationCircleOutlined, LockOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import PatientBasicDetails from "./patientBasicDetails";
import RpmDetails from "./providerDetails";
import ProgramInsurance from "./programAndInsurance";
import VitalConfiguration from "./vitalConfiguration";
import Devices from "./RpmDevices";
import img1 from "../../../../Assets/RegistrationAssets/Vector1.png";
import img2 from "../../../../Assets/RegistrationAssets/Vector2.png";
import img3 from "../../../../Assets/RegistrationAssets/Vector3.png";
import img4 from "../../../../Assets/RegistrationAssets/Vector4.png";
import img5 from "../../../../Assets/RegistrationAssets/Vector5.png";

const STEPS = [
  { id: 1, title: "Basic Details", icon: img1 },
  { id: 2, title: "Provider", icon: img2 },
  { id: 3, title: "Program / Insurance", icon: img3 },
  { id: 4, title: "Configuration", icon: img4 },
  { id: 5, title: "Devices", icon: img5 },
];

const getValidationRules = (payload) => [
  { tab: 1, message: "First name is required", invalid: !payload?.firstName },
  { tab: 1, message: "Last name is required", invalid: !payload?.lastName },
  { tab: 1, message: "Mobile number is required", invalid: !payload?.mobile },
  { tab: 1, message: "Mobile must be 10 digits", invalid: payload?.mobile && !/^\d{10,15}$/.test(payload.mobile) },
  { tab: 1, message: "Email is invalid", invalid: payload?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email) },
  { tab: 1, message: "SOS: Contact 1 is required", invalid: payload?.sos === 'Y' && !payload?.erContactNo1 },
  { tab: 1, message: "SOS: Contact 2 is required", invalid: payload?.sos === 'Y' && !payload?.erContactNo2 },
  { tab: 1, message: "SOS: Contact 3 is required", invalid: payload?.sos === 'Y' && !payload?.erContactNo3 },
  { tab: 1, message: "SOS: Contact 4 is required", invalid: payload?.sos === 'Y' && !payload?.erContactNo4 },
  { tab: 1, message: "Watch ID is required", invalid: payload?.rpmSmartWatch === 'Y' && !payload?.smartWatchUserId },
  { tab: 1, message: "Omron ID is required", invalid: payload?.omranConnectYN === 'Y' && !payload?.omranConnectId },
  { tab: 2, message: "Hospital is required", invalid: payload?.rpmApplicable === 'Y' && !payload?.hospitalId },
  { tab: 2, message: "Location is required", invalid: payload?.rpmApplicable === 'Y' && !payload?.locationId },
  { tab: 2, message: "Provider is required", invalid: payload?.rpmApplicable === 'Y' && !payload?.providerCode },
  {
    tab: 3,
    message: "Please upload all required consent forms",
    invalid:
      payload?.rpmApplicable === "Y" &&
      (!payload?.patientRegConsentDtlList ||
        ["PHYSICIAN_ORDER", "DEVICE_RESP", "PATIENT_DATA", "PROGRAM"].some(
          (code) =>
            !payload.patientRegConsentDtlList.some(
              (c) => c.consentCode === code
            )
        )),
  },
];

function Index({ handleCancel, handleSave }) {
  const dispatch = useDispatch();
  const [currPageView, setCurrPageView] = useState(1);
  const [completedTabs, setCompletedTabs] = useState(new Set());
  const [tabErrors, setTabErrors] = useState({});
  const { registrationPayload, selectedEditPatient } = useSelector(
    (state) => state.patients,
  );
  const storedLoginResult = sessionStorage.getItem("login_result");
  const loginResultObject = storedLoginResult
    ? JSON.parse(storedLoginResult)
    : null;
  const canUseRpm = loginResultObject?.user?.rpmApplicable === "Y";
  const isRpmEnabled =
    registrationPayload?.rpmApplicable === "Y" ||
    (!registrationPayload && selectedEditPatient?.rpmApplicable === "Y");

  const tab1Ref = useRef();
  const tab2Ref = useRef();
  const tab3Ref = useRef();
  const tab4Ref = useRef();
  const tab5Ref = useRef();
  const tabRefs = { 1: tab1Ref, 2: tab2Ref, 3: tab3Ref, 4: tab4Ref, 5: tab5Ref };

  const collectAndSync = async (tabId) => {
    const ref = tabRefs[tabId];
    if (ref?.current?.collectValues) await ref.current.collectValues();
  };

  const handleNext = async () => {
    await collectAndSync(currPageView);
    setCompletedTabs((previous) => new Set([...previous, currPageView]));
    setCurrPageView((previous) => Math.min(previous + 1, STEPS.length));
  };

  const handlePrevious = async () => {
    await collectAndSync(currPageView);
    setCurrPageView((previous) => Math.max(previous - 1, 1));
  };

  const handleTabChange = async (stepId) => {
    if (stepId === currPageView) return;
    await collectAndSync(currPageView);
    setCurrPageView(stepId);
  };

  const handleCancelWithClear = () => {
    handleCancel();
    setTabErrors({});
    setCompletedTabs(new Set());
  };

  const handleFinalSave = async (finalPayload) => {
    const errors = {};
    getValidationRules(finalPayload).forEach(({ tab, message, invalid }) => {
      if (invalid) {
        if (!errors[tab]) errors[tab] = [];
        errors[tab].push(message);
      }
    });
    if (Object.keys(errors).length > 0) {
      setTabErrors(errors);
      setCurrPageView(Math.min(...Object.keys(errors).map(Number)));
      return false;
    }
    setTabErrors({});
    return true;
  };

  const renderStep = (step) => {
    const isCompleted = completedTabs.has(step.id);
    const isActive = currPageView === step.id;
    const hasError = tabErrors[step.id]?.length > 0;
    const isLocked = step.id > 1 && !isRpmEnabled;

    const circleClass = hasError ? "border-red-500   bg-red-50   text-red-500"
      : isActive ? "border-teal-500  bg-white    text-teal-500"
        : isCompleted ? "border-teal-500  bg-teal-500 text-white"
          : isLocked ? "border-gray-200  bg-gray-50  text-gray-300"
            : "border-gray-300   bg-white    text-gray-400";

    const labelClass = hasError ? "text-red-500"
      : isLocked ? "text-gray-300"
        : isActive || isCompleted ? "text-black"
          : "text-gray-400";

    return (
      <div key={step.id} className="flex flex-col items-center gap-1">
        <div
          className="relative cursor-pointer select-none group"
          onClick={() => handleTabChange(step.id)}
        >
          <div className={`w-9 h-9 flex items-center justify-center rounded-full border-2 transition-all duration-200 ${circleClass}`}>
            {hasError
              ? <ExclamationCircleOutlined />
              : isCompleted && !isLocked && !isActive
                ? <CheckOutlined />
                : <img src={step.icon} alt={step.title} className={isLocked ? "opacity-30" : ""} />
            }
          </div>

          {isLocked && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gray-400 flex items-center justify-center shadow">
              <LockOutlined style={{ fontSize: 9, color: '#fff' }} />
            </span>
          )}

          {hasError && (
            <div className="absolute left-1/2 -translate-x-1/2 top-11 z-50 hidden group-hover:block
                                        bg-red-600 text-white text-xs rounded-md p-2 shadow-lg w-44 pointer-events-none">
              {tabErrors[step.id].map((msg, i) => (
                <p key={i} className="leading-5">• {msg}</p>
              ))}
            </div>
          )}

          {isLocked && !hasError && (
            <div className="absolute left-1/2 -translate-x-1/2 top-11 z-50 hidden group-hover:block
                                        bg-gray-700 text-white text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap pointer-events-none">
              Enable RPM in Basic Details
            </div>
          )}
        </div>

        <button
          className={`text-xs font-medium cursor-pointer bg-white  select-none ${labelClass}`}
          onClick={() => handleTabChange(step.id)}
        >
          {step.title}
        </button>
      </div>
    );
  };

  return (
    <div className="pl-5">
      {canUseRpm && (
        <div className="w-[80%] mx-auto my-4 px-4 pt-2">
          <div className="flex justify-between items-start w-full mb-1">
            {STEPS.map(renderStep)}
          </div>
          <div className="relative w-full h-1 bg-gray-200 rounded-full mt-3">
            <div
              className="absolute h-1 bg-teal-500 rounded-full transition-all duration-500"
              style={{
                width: `${((currPageView - 1) / (STEPS.length - 1)) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      <div>
        <div className={currPageView === 1 ? "block" : "hidden"}>
          <PatientBasicDetails
            ref={tab1Ref}
            handleCancel={handleCancelWithClear}
            handleSave={handleSave}
            handleNext={handleNext}
          />
        </div>
        <div className={currPageView === 2 ? "block" : "hidden"}>
          <RpmDetails
            ref={tab2Ref}
            currPageView={currPageView}
            handleCancel={handleCancelWithClear}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            isRpmEnabled={isRpmEnabled}
          />
        </div>
        <div className={currPageView === 3 ? "block" : "hidden"}>
          <ProgramInsurance
            ref={tab3Ref}
            currPageView={currPageView}
            handleCancel={handleCancelWithClear}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            isRpmEnabled={isRpmEnabled}
          />
        </div>
        <div className={currPageView === 4 ? "block" : "hidden"}>
          <VitalConfiguration
            ref={tab4Ref}
            currPageView={currPageView}
            handleCancel={handleCancelWithClear}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            isRpmEnabled={isRpmEnabled}
          />
        </div>
        <div className={currPageView === 5 ? "block" : "hidden"}>
          <Devices
            ref={tab5Ref}
            currPageView={currPageView}
            handleCancel={handleCancelWithClear}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            handleFinalSave={handleFinalSave}
            isRpmEnabled={isRpmEnabled}
          />
        </div>
      </div>
    </div>
  );
}

export default Index;
